import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import api, { apiError } from "../lib/api";
import { toast } from "sonner";
import { Coins, MessageSquare, ScanLine, Ruler, TrendingUp, Hammer, Lock, Gift, Plus } from "lucide-react";

const FEATURE_META = {
  trends: { label: "Trends", icon: TrendingUp },
  scan: { label: "Scan", icon: ScanLine },
  measure: { label: "Measure", icon: Ruler },
  design: { label: "Design", icon: Hammer },
};

export function UsageBar() {
  const { usage, refreshUsage, refresh, user, setUsage } = useAuth();
  const loc = useLocation();
  const [showRedeem, setShowRedeem] = useState(false);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [buying, setBuying] = useState(false);

  useEffect(() => {
    if (user?.paid) refreshUsage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loc.pathname, user?.paid]);

  if (!usage || !usage.applies) return null;

  const redeem = async () => {
    if (!code.trim()) return;
    setBusy(true);
    try {
      const { data } = await api.post("/billing/redeem", { code: code.trim() });
      if (data.coins) {
        if (data.usage) setUsage(data.usage); else refreshUsage();
        toast.success(`+${data.added} coins added`);
      } else {
        toast.success("Code redeemed");
        refresh();
      }
      setCode("");
      setShowRedeem(false);
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const buyCoins = async () => {
    setBuying(true);
    try {
      const { data } = await api.post("/payments/coins/checkout", { origin_url: window.location.origin });
      if (data.checkout_url) window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
      setBuying(false);
    }
  };

  return (
    <div className="pm-card p-3 sm:p-4 mb-4" data-testid="usage-bar">
      <div className="flex flex-wrap items-center gap-x-6 gap-y-3">
        {/* Coins */}
        <div className="flex items-center gap-2" data-testid="usage-coins">
          <Coins className="w-5 h-5" style={{ color: "var(--earn)" }} />
          <span className="label-mono">Coins</span>
          <span className="font-head font-extrabold text-lg" data-testid="usage-coins-value">{usage.coins_remaining}</span>
          {usage.coins_purchased > 0 && (
            <span className="label-mono" data-testid="usage-coins-breakdown">({usage.coins_monthly} monthly + {usage.coins_purchased} bought)</span>
          )}
        </div>

        {/* Feature costs */}
        <div className="flex flex-wrap items-center gap-2">
          {Object.entries(FEATURE_META).map(([key, { label, icon: Icon }]) => {
            const f = usage.features?.[key];
            if (!f) return null;
            return (
              <span
                key={key}
                className="badge flex items-center gap-1.5"
                data-testid={`usage-feature-${key}`}
                title={f.available ? `${f.cost} coins` : "Not enough coins"}
                style={{ background: "var(--surface-subtle)", color: f.available ? "var(--text)" : "var(--text-2)" }}
              >
                <Icon className="w-3 h-3" /> {label}
                {f.available ? <span className="label-mono">{f.cost}</span> : <Lock className="w-3 h-3" />}
              </span>
            );
          })}
          <span className="badge flex items-center gap-1.5" data-testid="usage-feature-mentor" title="Each mentor message">
            <MessageSquare className="w-3 h-3" /> Mentor <span className="label-mono">{usage.mentor_cost}</span>
          </span>
        </div>

        <div className="flex items-center gap-2 ml-auto">
          <button onClick={buyCoins} disabled={buying} className="pm-btn text-xs flex items-center gap-1.5" data-testid="usage-buy-coins">
            <Plus className="w-3.5 h-3.5" /> {buying ? "Opening…" : "Buy 50 coins"}
          </button>
          <button onClick={() => setShowRedeem((s) => !s)} className="pm-btn-ghost text-xs flex items-center gap-1.5" data-testid="usage-redeem-toggle">
            <Gift className="w-3.5 h-3.5" /> Code
          </button>
        </div>
      </div>

      {showRedeem && (
        <div className="mt-3 flex flex-wrap gap-2 items-center" data-testid="usage-redeem-form">
          <input
            className="pm-input max-w-[220px]"
            placeholder="Enter coin code"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && redeem()}
            data-testid="usage-redeem-input"
          />
          <button onClick={redeem} disabled={busy} className="pm-btn text-sm" data-testid="usage-redeem-submit">{busy ? "Redeeming…" : "Redeem"}</button>
        </div>
      )}

      <p className="text-xs mt-2" style={{ color: "var(--text-2)" }}>
        {usage.monthly_grant} free coins a month (unused monthly coins don't roll over). Buy more anytime — purchased coins never expire.
      </p>
    </div>
  );
}
